import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  Ban,
  Edit2,
  EllipsisVertical,
  RotateCcw,
  RotateCw,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import {
  inviteMemberInputSchema,
  ssoInviteMemberInputSchema,
  type InviteMemberInput,
  type OrganizationInvitation,
  type OrganizationMember,
  type SsoInviteMemberInput,
} from "@repo/shared";
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  DataTable,
  DialogClose,
  FeedbackState,
  Field,
  Form,
  FormActions,
  FormDrawer,
  Input,
  localTablePage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  StatusBadge,
  TableIdentity,
  UserChip,
  type ActionMenuItem,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import { organizationMembershipsQueryOptions } from "@/features/organizations/api";
import {
  membersQueryOptions,
  invitationsQueryOptions,
  useInviteMember,
  useInviteMemberViaH2j,
  useResendInvitation,
  useCancelInvitation,
  useResendMemberInvitation,
  useCancelMemberInvitation,
  useRemoveMember,
  useUpdateMemberRole,
  useUpdateMemberStatus,
} from "@/features/organizations/members-api";
import { useSsoStatus } from "@/features/sso/api";
import { getStoredActiveOrganizationId, getErrorMessage } from "@/lib/api-client";
import { ssoErrorMessage } from "@/lib/sso";
import { discardMessages, listRefreshing, paginationLabels, tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { useFormatDate } from "@/hooks/use-format-date";

export const Route = createFileRoute("/_authenticated/settings/members")({
  staticData: { pageTitle: "breadcrumbs.orgMembers" },
  component: MembersSection,
});

// The smallest page-size option: a roster that fits in one page shows no pagination bar.
const MIN_PAGE_SIZE = 25;

const INITIAL_TABLE_STATE: DataTableState = {
  page: 1,
  pageSize: 25,
  query: "",
  filters: {},
  selection: [],
};

type MenuAction = {
  id: string;
  label: string;
  icon: ReactNode;
  destructive?: boolean;
  /** Receives the row's kebab, where focus returns once the drawer or dialog it opens closes. */
  onSelect: (trigger: HTMLElement | null) => void;
};

/** The kebab of a table row: the library ActionMenu, separating the destructive action. */
function RowMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const trigger = useRef<HTMLButtonElement>(null);
  const items = actions.flatMap((action, index) => {
    const item: ActionMenuItem = {
      id: action.id,
      label: action.label,
      icon: action.icon,
      destructive: action.destructive,
      onSelect: () => action.onSelect(trigger.current),
    };
    return action.destructive && index > 0
      ? [{ type: "separator" as const, id: `separator-${action.id}` }, item]
      : [item];
  });

  return (
    <ActionMenu
      label={label}
      items={items}
      trigger={
        <Button ref={trigger} variant="ghost" size="icon-sm" aria-label={label} title={label}>
          <EllipsisVertical />
        </Button>
      }
    />
  );
}

function EditMemberDrawer({
  member,
  open,
  onClose,
  organizationId,
  restoreFocusRef,
}: {
  member: OrganizationMember;
  open: boolean;
  onClose: () => void;
  organizationId: string;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const updateRoleMutation = useUpdateMemberRole(organizationId);
  const [selectedRole, setSelectedRole] = useState(member.role);

  const handleSave = async () => {
    try {
      await updateRoleMutation.mutateAsync({ memberId: member.id, role: selectedRole });
      toast.success(t("members.editDrawer.success"));
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, t("members.editDrawer.failed")));
    }
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="sm"
      title={t("members.editDrawer.title")}
      description={t("members.editDrawer.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      busy={updateRoleMutation.isPending}
      dirty={selectedRole !== member.role}
      discardConfirmation={discardMessages()}
      footer={
        <FormActions
          busy={updateRoleMutation.isPending}
          submitLabel={t("members.editDrawer.save")}
          submittingLabel={t("members.editDrawer.saving")}
          submitProps={{ form: "edit-member-form" }}
        >
          <DialogClose asChild>
            <Button variant="outline">{t("members.editDrawer.cancel")}</Button>
          </DialogClose>
        </FormActions>
      }
    >
      <Form
        id="edit-member-form"
        busy={updateRoleMutation.isPending}
        onSubmit={(event) => {
          event.preventDefault();
          void handleSave();
        }}
      >
        <UserChip name={member.user.name} detail={member.user.email} size="lg" />
        <Field label={t("members.editDrawer.roleLabel")}>
          {(control) => (
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger {...control}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{t("members.roles.member")}</SelectItem>
                <SelectItem value="admin">{t("members.roles.admin")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </Form>
    </FormDrawer>
  );
}

function InviteMemberDrawer({
  open,
  onOpenChange,
  organizationId,
  restoreFocusRef,
  sso,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  restoreFocusRef: RefObject<HTMLElement | null>;
  /** Login H2J is on: the invite goes through it and needs the person's name. */
  sso: boolean;
}) {
  const { t } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const inviteForm = useForm<SsoInviteMemberInput>({
    // Without Login H2J the name is not asked: the legacy schema validates e-mail and
    // role and drops the empty name from the data. Hence the cast on its output type.
    resolver: zodResolver(
      sso ? ssoInviteMemberInputSchema : inviteMemberInputSchema,
    ) as unknown as Resolver<SsoInviteMemberInput>,
    defaultValues: { email: "", name: "", role: "member" },
  });
  const inviteMemberMutation = useInviteMember(organizationId);
  const inviteViaH2jMutation = useInviteMemberViaH2j(organizationId);
  const pending = inviteMemberMutation.isPending || inviteViaH2jMutation.isPending;

  useEffect(() => {
    if (open) inviteForm.reset({ email: "", name: "", role: "member" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (data: SsoInviteMemberInput) => {
    try {
      if (sso) {
        const { status } = await inviteViaH2jMutation.mutateAsync(data);
        toast.success(
          t(status === "added" ? "members.invite.successAdded" : "members.invite.successH2j", {
            email: data.email,
          }),
        );
      } else {
        await inviteMemberMutation.mutateAsync(data);
        toast.success(t("members.invite.success", { email: data.email }));
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        sso
          ? ssoErrorMessage(err, t("members.invite.failed"))
          : getErrorMessage(err, t("members.invite.failed")),
      );
    }
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t("members.invite.title")}
      description={t(sso ? "members.invite.descriptionH2j" : "members.invite.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      busy={pending}
      dirty={inviteForm.formState.isDirty}
      discardConfirmation={discardMessages()}
      footer={
        <FormActions
          busy={pending}
          submitLabel={t("members.invite.sendInvite")}
          submittingLabel={t("members.invite.sending")}
          submitProps={{ form: "invite-member-form" }}
        >
          <DialogClose asChild>
            <Button variant="outline">{t("members.editDrawer.cancel")}</Button>
          </DialogClose>
        </FormActions>
      }
    >
      <Form id="invite-member-form" busy={pending} onSubmit={inviteForm.handleSubmit(onSubmit)}>
        <Field
          label={t("members.table.colEmail")}
          error={inviteForm.formState.errors.email?.message}
        >
          <Input
            type="email"
            placeholder={t("members.invite.emailPlaceholder")}
            {...inviteForm.register("email")}
          />
        </Field>
        {sso && (
          <Field
            label={t("members.invite.nameLabel")}
            error={inviteForm.formState.errors.name?.message}
          >
            <Input
              autoComplete="off"
              maxLength={120}
              placeholder={t("members.invite.namePlaceholder")}
              {...inviteForm.register("name")}
            />
          </Field>
        )}
        <Field label={t("members.invite.roleLabel")}>
          {(control) => (
            <Select
              value={inviteForm.watch("role")}
              onValueChange={(value) =>
                inviteForm.setValue("role", value as SsoInviteMemberInput["role"], {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger {...control}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{t("members.roles.member")}</SelectItem>
                <SelectItem value="admin">{t("members.roles.admin")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </Form>
    </FormDrawer>
  );
}

function MembersSection() {
  const { t } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const { user } = Route.useRouteContext();
  const { formatDate } = useFormatDate();
  const { confirm, dialogProps } = useConfirmDialog();
  const { density } = useTableDensity();
  // Drawers and dialogs opened from a row's menu return focus to that row's kebab.
  const returnFocus = useRef<HTMLElement | null>(null);
  const inviteButton = useRef<HTMLButtonElement>(null);
  // The edited member keeps rendering while its drawer slides out; a new key per
  // opening restarts the role picker from the member's current role.
  const [editing, setEditing] = useState<{ member: OrganizationMember; key: number } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [membersState, setMembersState] = useState(INITIAL_TABLE_STATE);
  const [invitationsState, setInvitationsState] = useState(INITIAL_TABLE_STATE);

  const { data: organizationMemberships = [], isLoading } = useQuery(
    organizationMembershipsQueryOptions,
  );
  const activeOrganizationId = getStoredActiveOrganizationId() ?? "";
  const activeOrganization =
    organizationMemberships.find((org) => org.id === activeOrganizationId) ?? null;

  const isAdminOrOwner =
    activeOrganization?.role === "owner" || activeOrganization?.role === "admin";

  const membersQuery = useQuery(membersQueryOptions(activeOrganizationId));
  const invitationsQuery = useQuery(invitationsQueryOptions(activeOrganizationId));
  // Login H2J on: invites go through it, and people it invited show as pending activation.
  const { enabled: ssoEnabled } = useSsoStatus();

  const members = membersQuery.data?.data?.members ?? [];
  const invitations = (invitationsQuery.data?.data ?? []).filter((inv) => inv.status === "pending");

  const resendInvitationMutation = useResendInvitation(activeOrganizationId);
  const cancelInvitationMutation = useCancelInvitation(activeOrganizationId);
  const resendMemberInvitationMutation = useResendMemberInvitation();
  const cancelMemberInvitationMutation = useCancelMemberInvitation(activeOrganizationId);
  const removeMemberMutation = useRemoveMember(activeOrganizationId);
  const updateStatusMutation = useUpdateMemberStatus(activeOrganizationId);

  const roleLabel = (role: string) => t(`members.roles.${role}`, { defaultValue: role });

  const handleResendInvitation = async (email: string, role: InviteMemberInput["role"]) => {
    try {
      await resendInvitationMutation.mutateAsync({ email, role });
      toast.success(t("members.resendInvitation.success", { email }));
    } catch (err) {
      toast.error(getErrorMessage(err, t("members.resendInvitation.failed")));
    }
  };

  const handleCancelInvitation = (invitationId: string, email: string) => {
    confirm({
      title: t("members.cancelInvitation.title"),
      description: t("members.cancelInvitation.description", { email }),
      confirmLabel: t("members.cancelInvitation.confirmLabel"),
      variant: "destructive",
      restoreFocusRef: returnFocus,
      onConfirm: async () => {
        try {
          await cancelInvitationMutation.mutateAsync(invitationId);
          toast.success(t("members.cancelInvitation.success"));
        } catch (err) {
          toast.error(getErrorMessage(err, t("members.cancelInvitation.failed")));
        }
      },
    });
  };

  const handleResendMemberInvitation = async (member: OrganizationMember) => {
    try {
      await resendMemberInvitationMutation.mutateAsync(member.id);
      toast.success(t("members.resendInvitation.success", { email: member.user.email }));
    } catch (err) {
      toast.error(ssoErrorMessage(err, t("members.resendInvitation.failed")));
    }
  };

  const handleCancelMemberInvitation = (member: OrganizationMember) => {
    confirm({
      title: t("members.cancelInvitation.title"),
      description: t("members.cancelInvitation.memberDescription", {
        name: member.user.name,
        email: member.user.email,
      }),
      confirmLabel: t("members.cancelInvitation.confirmLabel"),
      variant: "destructive",
      restoreFocusRef: returnFocus,
      onConfirm: async () => {
        try {
          await cancelMemberInvitationMutation.mutateAsync(member.id);
          toast.success(t("members.cancelInvitation.success"));
        } catch (err) {
          toast.error(ssoErrorMessage(err, t("members.cancelInvitation.failed")));
        }
      },
    });
  };

  const handleRemoveMember = (memberId: string, name: string) => {
    confirm({
      title: t("members.removeMember.title"),
      description: t("members.removeMember.description", { name }),
      confirmLabel: t("members.removeMember.confirmLabel"),
      variant: "destructive",
      restoreFocusRef: returnFocus,
      onConfirm: async () => {
        try {
          await removeMemberMutation.mutateAsync(memberId);
          toast.success(t("members.removeMember.success", { name }));
        } catch (err) {
          toast.error(getErrorMessage(err, t("members.removeMember.failed")));
        }
      },
    });
  };

  const handleToggleStatus = (memberId: string, name: string, currentStatus: string) => {
    const deactivating = currentStatus !== "inactive";
    confirm({
      title: deactivating
        ? t("members.deactivateMember.title")
        : t("members.reactivateMember.title"),
      description: deactivating
        ? t("members.deactivateMember.description", { name })
        : t("members.reactivateMember.description", { name }),
      confirmLabel: deactivating
        ? t("members.deactivateMember.confirmLabel")
        : t("members.reactivateMember.confirmLabel"),
      variant: deactivating ? "destructive" : undefined,
      restoreFocusRef: returnFocus,
      onConfirm: async () => {
        try {
          await updateStatusMutation.mutateAsync({
            memberId,
            status: deactivating ? "inactive" : "active",
          });
          toast.success(
            deactivating
              ? t("members.deactivateMember.success", { name })
              : t("members.reactivateMember.success", { name }),
          );
        } catch (err) {
          toast.error(
            getErrorMessage(
              err,
              deactivating
                ? t("members.deactivateMember.failed")
                : t("members.reactivateMember.failed"),
            ),
          );
        }
      },
    });
  };

  if (isLoading) {
    return <FeedbackState state="loading" title={tc("loading")} />;
  }

  if (!activeOrganization) {
    return <FeedbackState state="empty" title={t("members.noOrgSelected")} />;
  }

  const memberColumns: DataTableColumn<OrganizationMember>[] = [
    {
      id: "member",
      header: t("members.table.colName"),
      variant: "identity",
      rowHeader: true,
      cell: (member) => (
        <TableIdentity
          leading={<Avatar size="sm" name={member.user.name} />}
          name={
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{member.user.name}</span>
              {member.user.email === user.email && (
                <Badge variant="neutral" className="shrink-0">
                  {t("members.you")}
                </Badge>
              )}
            </span>
          }
          detail={member.user.email}
        />
      ),
    },
    {
      id: "role",
      header: t("members.table.colRole"),
      width: "14rem",
      cell: (member) => (
        <span className="flex items-center gap-2">
          <Badge variant={member.role === "owner" ? "default" : "secondary"}>
            {roleLabel(member.role)}
          </Badge>
          {member.status === "inactive" && (
            <StatusBadge tone="neutral">{t("members.statusBadge.inactive")}</StatusBadge>
          )}
          {member.pendingActivation && (
            <StatusBadge tone="info">{t("members.statusBadge.pendingActivation")}</StatusBadge>
          )}
        </span>
      ),
    },
  ];

  const invitationColumns: DataTableColumn<OrganizationInvitation>[] = [
    {
      id: "email",
      header: t("members.table.colEmail"),
      rowHeader: true,
      cell: (invitation) => <span className="font-mono">{invitation.email}</span>,
    },
    {
      id: "role",
      header: t("members.table.colRole"),
      width: "14rem",
      cell: (invitation) => <Badge variant="secondary">{roleLabel(invitation.role)}</Badge>,
    },
    {
      id: "expires",
      header: t("members.table.colExpires"),
      width: "12rem",
      variant: "numeric",
      cell: (invitation) => formatDate(invitation.expiresAt),
    },
  ];

  const membersPage = localTablePage(members, membersState, { columns: memberColumns });
  const invitationsPage = localTablePage(invitations, invitationsState, {
    columns: invitationColumns,
  });

  return (
    <>
      <SettingsSection
        title={t("members.table.title")}
        description={
          membersQuery.isLoading
            ? t("members.table.loadingCount")
            : t("members.table.count", { count: members.length })
        }
        actions={
          isAdminOrOwner ? (
            <Button ref={inviteButton} size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus />
              {t("members.invite.title")}
            </Button>
          ) : undefined
        }
      >
        <DataTable
          label={t("members.table.title")}
          bordered={false}
          density={density}
          columns={memberColumns}
          rows={membersPage.rows}
          getRowId={(member) => member.id}
          state={membersState}
          onStateChange={setMembersState}
          loading={membersQuery.isPending}
          refreshing={listRefreshing(membersQuery)}
          error={membersQuery.isError ? { title: tc("error.loadFailed") } : null}
          onRetry={() => void membersQuery.refetch()}
          pagination={
            membersPage.total > MIN_PAGE_SIZE
              ? { kind: "offset", total: membersPage.total, labels: paginationLabels() }
              : undefined
          }
          rowActions={
            isAdminOrOwner
              ? {
                  label: t("members.table.actions"),
                  render: (member) => {
                    const canManage = member.role !== "owner" && member.user.email !== user.email;
                    if (!canManage) return null;
                    const inactive = member.status === "inactive";
                    // Waiting on Login H2J: the invitation is what gets resent or cancelled
                    // (cancelling also takes them out of the organization), so no
                    // deactivate or remove.
                    const pending = member.pendingActivation === true;
                    return (
                      <RowMenu
                        label={t("members.table.rowActions", { name: member.user.name })}
                        actions={[
                          {
                            id: "edit",
                            label: t("members.table.edit"),
                            icon: <Edit2 />,
                            onSelect: (trigger) => {
                              returnFocus.current = trigger;
                              setEditing((current) => ({
                                member,
                                key: (current?.key ?? 0) + 1,
                              }));
                              setEditOpen(true);
                            },
                          },
                          ...(pending
                            ? [
                                {
                                  id: "resend-invitation",
                                  label: t("members.table.resendInvitation"),
                                  icon: <RotateCw />,
                                  onSelect: () => void handleResendMemberInvitation(member),
                                },
                                {
                                  id: "cancel-invitation",
                                  label: t("members.table.cancelInvitation"),
                                  icon: <X />,
                                  destructive: true,
                                  onSelect: (trigger: HTMLElement | null) => {
                                    returnFocus.current = trigger;
                                    handleCancelMemberInvitation(member);
                                  },
                                },
                              ]
                            : [
                                {
                                  id: "status",
                                  label: inactive
                                    ? t("members.table.reactivate")
                                    : t("members.table.deactivate"),
                                  icon: inactive ? <RotateCcw /> : <Ban />,
                                  destructive: !inactive,
                                  onSelect: (trigger: HTMLElement | null) => {
                                    returnFocus.current = trigger;
                                    handleToggleStatus(member.id, member.user.name, member.status);
                                  },
                                },
                                {
                                  id: "remove",
                                  label: t("members.table.remove"),
                                  icon: <Trash2 />,
                                  destructive: true,
                                  onSelect: (trigger: HTMLElement | null) => {
                                    returnFocus.current = trigger;
                                    handleRemoveMember(member.id, member.user.name);
                                  },
                                },
                              ]),
                        ]}
                      />
                    );
                  },
                }
              : undefined
          }
          messages={tableMessages({ empty: { title: t("members.table.empty") } })}
        />
      </SettingsSection>

      {isAdminOrOwner && (
        <SettingsSection
          title={t("members.pendingInvitations.title")}
          description={
            invitationsQuery.isLoading
              ? t("members.pendingInvitations.loading")
              : invitations.length === 0
                ? t("members.pendingInvitations.none")
                : t("members.pendingInvitations.count", { count: invitations.length })
          }
        >
          <DataTable
            label={t("members.pendingInvitations.title")}
            bordered={false}
            density={density}
            columns={invitationColumns}
            rows={invitationsPage.rows}
            getRowId={(invitation) => invitation.id}
            state={invitationsState}
            onStateChange={setInvitationsState}
            loading={invitationsQuery.isPending}
            refreshing={listRefreshing(invitationsQuery)}
            error={invitationsQuery.isError ? { title: tc("error.loadFailed") } : null}
            onRetry={() => void invitationsQuery.refetch()}
            pagination={
              invitationsPage.total > MIN_PAGE_SIZE
                ? { kind: "offset", total: invitationsPage.total, labels: paginationLabels() }
                : undefined
            }
            rowActions={{
              label: t("members.table.actions"),
              render: (invitation) => (
                <RowMenu
                  label={t("members.table.rowActions", { name: invitation.email })}
                  actions={[
                    // Login H2J e-mails the activation link, not Better Auth: resending
                    // is done from the member's row.
                    ...(ssoEnabled
                      ? []
                      : [
                          {
                            id: "resend",
                            label: t("members.pendingInvitations.resend"),
                            icon: <RotateCw />,
                            onSelect: () =>
                              void handleResendInvitation(
                                invitation.email,
                                invitation.role as InviteMemberInput["role"],
                              ),
                          },
                        ]),
                    {
                      id: "cancel",
                      label: t("members.pendingInvitations.cancelAction"),
                      icon: <X />,
                      destructive: true,
                      onSelect: (trigger) => {
                        returnFocus.current = trigger;
                        handleCancelInvitation(invitation.id, invitation.email);
                      },
                    },
                  ]}
                />
              ),
            }}
            messages={tableMessages({ empty: { title: t("members.pendingInvitations.none") } })}
          />
        </SettingsSection>
      )}

      <ConfirmDialog {...dialogProps} />

      <InviteMemberDrawer
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        organizationId={activeOrganizationId}
        restoreFocusRef={inviteButton}
        sso={ssoEnabled}
      />

      {editing && (
        <EditMemberDrawer
          key={editing.key}
          member={editing.member}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          organizationId={activeOrganizationId}
          restoreFocusRef={returnFocus}
        />
      )}
    </>
  );
}
